/**
 * Why a card was refused, and the words we say about it.
 *
 * Two things were wrong with what this replaces
 * (`console/src/lib/rainforest/decline-reason.ts`, deleted alongside this file):
 *
 * 1. It switched on an untyped normalised string, and `messageForCode`
 *    returned `null` for anything it did not list. The fallback interpolated
 *    the *processor's own* `refusal_desc` into patient-facing copy - so the
 *    words on screen were written by Rainforest, in Rainforest's register, and
 *    changed whenever they changed them.
 * 2. It was Rainforest-only, and Rainforest-shaped: `rainforest-save-payment-
 *    method.tsx` grew a second, unrelated ladder over `decline_*` event types
 *    because the SDK reports method declines in a different shape again.
 *
 * `DeclineClass` here is the same member list as `decoda/enums/decline_class.py`
 * - `test_decline_class_member_list_matches_console` fails the build if the two
 * drift. It has to be shared rather than merely similar, because a decline
 * reaches the console by two routes that never meet: through our API, where the
 * backend has already classified it, and straight from the card SDK in the
 * browser, which our API never sees. Both select from `MESSAGE` below.
 *
 * `MESSAGE` is a `Record<DeclineClass, string>`, so adding a class fails
 * typecheck until someone writes the copy for it.
 */
import { PaymentProcessor } from "@/types/auth";

export type DeclineClass =
  // The card will not work again.
  | "EXPIRED_CARD"
  | "LOST_OR_STOLEN"
  | "FRAUD_SUSPECTED"
  | "CARD_NOT_SUPPORTED"
  | "ACCOUNT_CLOSED"
  | "AUTHORIZATION_REVOKED"
  | "PAYMENT_METHOD_UNAVAILABLE"
  | "TOO_MANY_ATTEMPTS"
  // The patient can fix it without changing instrument.
  | "INCORRECT_CVC"
  | "INCORRECT_NUMBER"
  | "INCORRECT_ZIP"
  // Refused, but not definitively.
  | "INSUFFICIENT_FUNDS"
  | "DO_NOT_HONOR"
  | "CARD_DECLINED"
  // Not a verdict on the card at all.
  | "AUTHENTICATION_REQUIRED"
  | "PROCESSOR_UNAVAILABLE"
  | "REQUEST_INVALID"
  | "UNKNOWN";

/**
 * Rainforest refusal codes, keyed with any `DECLINE_` prefix already stripped.
 *
 * Covers both shapes the SDK emits: `refusal_code` on a payin event
 * (`LOST_CARD`) and `event_type` on a payment-method event
 * (`decline_lost_card`), which normalise to the same key.
 */
const RAINFOREST_DECLINE_CLASS: Record<string, DeclineClass> = {
  INSUFFICIENT_FUNDS: "INSUFFICIENT_FUNDS",
  EXPIRED_CARD: "EXPIRED_CARD",
  LOST_CARD: "LOST_OR_STOLEN",
  STOLEN_CARD: "LOST_OR_STOLEN",
  PICKUP_CARD: "LOST_OR_STOLEN",
  FRAUD: "FRAUD_SUSPECTED",
  FRAUDULENT: "FRAUD_SUSPECTED",
  INVALID_CARD: "PAYMENT_METHOD_UNAVAILABLE",
  CLOSED_ACCOUNT: "ACCOUNT_CLOSED",
  REVOKED_AUTHORIZATION: "AUTHORIZATION_REVOKED",
  DO_NOT_HONOR: "DO_NOT_HONOR",
  DECLINED: "CARD_DECLINED",
  CARD_DECLINED: "CARD_DECLINED",
  // `decline_cvc` is the SDK's method-event spelling; the payin events use the
  // longer forms.
  CVC: "INCORRECT_CVC",
  INCORRECT_CVC: "INCORRECT_CVC",
  INVALID_CVC: "INCORRECT_CVC",
  CVV_FAILURE: "INCORRECT_CVC",
  INVALID_CARD_SECURITY_CODE: "INCORRECT_CVC",
  AVS_FAILURE: "INCORRECT_ZIP",
  INCORRECT_ZIP: "INCORRECT_ZIP",
  INVALID_NUMBER: "INCORRECT_NUMBER",
  INCORRECT_NUMBER: "INCORRECT_NUMBER",
  PROCESSING_ERROR: "PROCESSOR_UNAVAILABLE",
  PROCESSOR_ERROR: "PROCESSOR_UNAVAILABLE",
  ISSUER_UNAVAILABLE: "PROCESSOR_UNAVAILABLE",
  TRY_AGAIN_LATER: "PROCESSOR_UNAVAILABLE",
  SYSTEM_ERROR: "PROCESSOR_UNAVAILABLE",
  TIMEOUT: "PROCESSOR_UNAVAILABLE",
};

/** Stripe `error.code`, which is the more specific of its two fields. */
const STRIPE_CODE_CLASS: Record<string, DeclineClass> = {
  expired_card: "EXPIRED_CARD",
  card_decline_rate_limit_exceeded: "TOO_MANY_ATTEMPTS",
  incorrect_cvc: "INCORRECT_CVC",
  invalid_cvc: "INCORRECT_CVC",
  incorrect_number: "INCORRECT_NUMBER",
  invalid_number: "INCORRECT_NUMBER",
  incorrect_zip: "INCORRECT_ZIP",
  processing_error: "PROCESSOR_UNAVAILABLE",
  issuer_not_available: "PROCESSOR_UNAVAILABLE",
  try_again_later: "PROCESSOR_UNAVAILABLE",
  authentication_required: "AUTHENTICATION_REQUIRED",
  // Stripe.js rejects a form it can see is wrong *before* any network call,
  // with `type: "validation_error"` and these codes. They never reached a bank,
  // so classing them as declines told a patient their issuer had refused them -
  // "contact your financial institution" - over a mistyped ZIP.
  invalid_zip: "INCORRECT_ZIP",
  incomplete_zip: "INCORRECT_ZIP",
  incomplete_number: "INCORRECT_NUMBER",
  incomplete_cvc: "INCORRECT_CVC",
  incomplete_expiry: "EXPIRED_CARD",
  invalid_expiry_month: "EXPIRED_CARD",
  invalid_expiry_year: "EXPIRED_CARD",
  invalid_expiry_year_past: "EXPIRED_CARD",
};

/** Stripe `error.decline_code`, which names the reason `code` generalises. */
const STRIPE_DECLINE_CLASS: Record<string, DeclineClass> = {
  insufficient_funds: "INSUFFICIENT_FUNDS",
  lost_card: "LOST_OR_STOLEN",
  stolen_card: "LOST_OR_STOLEN",
  pickup_card: "LOST_OR_STOLEN",
  fraudulent: "FRAUD_SUSPECTED",
  merchant_blacklist: "FRAUD_SUSPECTED",
  restricted_card: "CARD_NOT_SUPPORTED",
  card_not_supported: "CARD_NOT_SUPPORTED",
  currency_not_supported: "CARD_NOT_SUPPORTED",
  transaction_not_allowed: "CARD_NOT_SUPPORTED",
  not_permitted: "CARD_NOT_SUPPORTED",
  revocation_of_all_authorizations: "AUTHORIZATION_REVOKED",
  revocation_of_authorization: "AUTHORIZATION_REVOKED",
  stop_payment_order: "AUTHORIZATION_REVOKED",
  no_action_taken: "AUTHORIZATION_REVOKED",
  do_not_honor: "DO_NOT_HONOR",
  generic_decline: "CARD_DECLINED",
  processing_error: "PROCESSOR_UNAVAILABLE",
  issuer_not_available: "PROCESSOR_UNAVAILABLE",
  try_again_later: "PROCESSOR_UNAVAILABLE",
  reenter_transaction: "PROCESSOR_UNAVAILABLE",
  authentication_required: "AUTHENTICATION_REQUIRED",
  // Stripe.js rejects a form it can see is wrong *before* any network call,
  // with `type: "validation_error"` and these codes. They never reached a bank,
  // so classing them as declines told a patient their issuer had refused them -
  // "contact your financial institution" - over a mistyped ZIP.
  invalid_zip: "INCORRECT_ZIP",
  incomplete_zip: "INCORRECT_ZIP",
  incomplete_number: "INCORRECT_NUMBER",
  incomplete_cvc: "INCORRECT_CVC",
  incomplete_expiry: "EXPIRED_CARD",
  invalid_expiry_month: "EXPIRED_CARD",
  invalid_expiry_year: "EXPIRED_CARD",
  invalid_expiry_year_past: "EXPIRED_CARD",
};

/**
 * The copy is ours, keyed by class. The processor's own text is never rendered.
 *
 * Every string ends by saying whether money moved, because that is the first
 * question the person at the desk has and the old generic fallback answered it
 * only by accident.
 */
export const MESSAGE: Record<DeclineClass, string> = {
  EXPIRED_CARD:
    "The card issuer declined this payment because the card has expired. Use another payment method. No charge was made.",
  LOST_OR_STOLEN:
    "The card issuer declined this payment because the card was reported lost or stolen. Ask the patient to contact the card issuer or use another payment method. No charge was made.",
  FRAUD_SUSPECTED:
    "The card issuer declined this payment as suspected fraud. Ask the patient to contact their financial institution, or use another payment method. No charge was made.",
  CARD_NOT_SUPPORTED:
    "This card cannot be used for this type of payment. Use another payment method. No charge was made.",
  ACCOUNT_CLOSED:
    "The account behind this card is closed. Use another payment method. No charge was made.",
  AUTHORIZATION_REVOKED:
    "The patient's financial institution has withdrawn permission to charge this card. Ask the patient for another payment method. No charge was made.",
  PAYMENT_METHOD_UNAVAILABLE:
    "This saved payment method can no longer be used. Ask the patient for another payment method. No charge was made.",
  TOO_MANY_ATTEMPTS:
    "This card has been declined too many times recently and further attempts are blocked. Use another payment method, or try this card again in a day. No charge was made.",
  INCORRECT_CVC:
    "The card security code was not accepted. Check the CVC and try again. No charge was made.",
  INCORRECT_NUMBER:
    "The card number was not accepted. Check the number and try again. No charge was made.",
  INCORRECT_ZIP:
    "The billing ZIP code did not match the one on file with the card issuer. Check the ZIP code and try again. No charge was made.",
  INSUFFICIENT_FUNDS:
    "The patient's financial institution declined this payment because there are insufficient available funds. Ask the patient to check the available balance or use another payment method. No charge was made.",
  DO_NOT_HONOR:
    "The patient's financial institution declined this payment without giving a reason. Ask the patient to contact their financial institution, or try another payment method. No charge was made.",
  CARD_DECLINED:
    "The patient's financial institution or payment processor declined this payment. Try another payment method, or ask the patient to contact their financial institution. No charge was made.",
  AUTHENTICATION_REQUIRED:
    "The patient's financial institution needs them to verify this payment. Ask the patient to complete verification with their banking app, then try again. No charge was made.",
  PROCESSOR_UNAVAILABLE:
    "The payment processor could not complete this payment. Try again or use another payment method. No charge was made.",
  // Deliberately worded as our fault, not the card's. This is the class that
  // must never cancel a payment plan or read to staff as the patient's problem.
  REQUEST_INVALID:
    "This payment could not be sent for processing. Please try again — if it keeps happening, contact support. No charge was made.",
  UNKNOWN:
    "The payment was declined. Try another payment method, or ask the patient to contact their financial institution. No charge was made.",
};

export interface PaymentDecline {
  declineClass: DeclineClass;
  /** Our copy for `declineClass`. Safe to render.  */
  message: string;
  /**
   * The processor's own code, kept for logs and support - never rendered.
   * `null` when the payload carried nothing identifiable.
   */
  processorCode: string | null;
}

/**
 * The same classes, said to the person whose card it is.
 *
 * `MESSAGE` is written for staff standing at a desk - "the patient's financial
 * institution", "Ask the patient to…" - which is correct there and wrong on a
 * payment link, where the reader *is* the patient and was being told to ask
 * themselves. Rainforest's hosted component never hit this because it reports
 * its own short, neutral strings; Stripe's failures come through here instead.
 *
 * Same contract as `MESSAGE`: every string ends by saying whether money moved.
 */
export const PATIENT_MESSAGE: Record<DeclineClass, string> = {
  EXPIRED_CARD:
    "Your card issuer declined this payment because the card has expired. Try another payment method. You have not been charged.",
  LOST_OR_STOLEN:
    "Your card issuer declined this payment. Contact them, or try another payment method. You have not been charged.",
  FRAUD_SUSPECTED:
    "Your card issuer declined this payment. Contact them, or try another payment method. You have not been charged.",
  CARD_NOT_SUPPORTED:
    "This card cannot be used for this payment. Try another payment method. You have not been charged.",
  ACCOUNT_CLOSED:
    "The account behind this card is closed. Try another payment method. You have not been charged.",
  AUTHORIZATION_REVOKED:
    "Your financial institution has withdrawn permission to charge this card. Try another payment method. You have not been charged.",
  PAYMENT_METHOD_UNAVAILABLE:
    "This payment method can no longer be used. Try another one. You have not been charged.",
  TOO_MANY_ATTEMPTS:
    "This card has been declined too many times recently. Try another payment method, or this card again tomorrow. You have not been charged.",
  INCORRECT_CVC:
    "The card security code was not accepted. Check the CVC and try again. You have not been charged.",
  INCORRECT_NUMBER:
    "The card number was not accepted. Check the number and try again. You have not been charged.",
  INCORRECT_ZIP:
    "The billing ZIP code was not accepted. Check it and try again. You have not been charged.",
  INSUFFICIENT_FUNDS:
    "Your card issuer declined this payment because there are not enough available funds. Check your balance, or try another payment method. You have not been charged.",
  DO_NOT_HONOR:
    "Your card issuer declined this payment without giving a reason. Contact them, or try another payment method. You have not been charged.",
  CARD_DECLINED:
    "Your card was declined. Try another payment method, or contact your card issuer. You have not been charged.",
  AUTHENTICATION_REQUIRED:
    "Your financial institution needs you to verify this payment. Approve it in your banking app, then try again. You have not been charged.",
  PROCESSOR_UNAVAILABLE:
    "We could not complete this payment just now. Try again in a moment, or use another payment method. You have not been charged.",
  REQUEST_INVALID:
    "This payment could not be sent for processing. Please try again — if it keeps happening, contact the clinic. You have not been charged.",
  UNKNOWN:
    "This payment was declined. Try another payment method, or contact your card issuer. You have not been charged.",
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (
  payload: UnknownRecord,
  ...keys: string[]
): string | null => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

/**
 * Dig the decline out of whichever envelope it arrived in.
 *
 * A decline reaches here as an axios error body (`{detail: …}`, sometimes a
 * validation-style array), as a Rainforest payin (`{data: …}`), or as a raw
 * SDK CustomEvent (`{detail: {event_type: …}}`). Unwrapping each layer once is
 * cheaper than teaching every caller which one it holds.
 */
const extractPayload = (input: unknown): UnknownRecord => {
  let payload: unknown = input;

  if (isRecord(payload) && "detail" in payload) {
    const detail = payload.detail;
    const inner = Array.isArray(detail) ? detail[0] : detail;
    // FastAPI's `{detail: "..."}` is the common shape here, so a bare string
    // has to keep its text rather than unwrapping into nothing.
    payload = typeof inner === "string" ? { message: inner } : inner;
  }
  if (isRecord(payload) && isRecord(payload.data)) {
    payload = payload.data;
  }
  return isRecord(payload) ? payload : {};
};

/** Upper-snake a code and drop any `DECLINE_` prefix, so the SDK's two spellings meet. */
const normalizeRainforestCode = (code: string | null): string | null => {
  if (!code) return null;
  const normalized = code
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^DECLINE_/, "");
  return normalized || null;
};

const classifyRainforest = (
  payload: UnknownRecord,
): { declineClass: DeclineClass | null; processorCode: string | null } => {
  const rawCode = readString(
    payload,
    "detailed_refusal_code",
    "detailedRefusalCode",
    "event_type",
    "eventType",
    "auth_code",
    "authCode",
    "refusal_code",
    "refusalCode",
  );
  const normalized = normalizeRainforestCode(rawCode);
  return {
    declineClass: normalized
      ? (RAINFOREST_DECLINE_CLASS[normalized] ?? "CARD_DECLINED")
      : null,
    processorCode: normalized,
  };
};

const classifyStripe = (
  payload: UnknownRecord,
): { declineClass: DeclineClass | null; processorCode: string | null } => {
  const error = isRecord(payload.error) ? payload.error : payload;
  const code = readString(error, "code");
  const declineCode = readString(error, "decline_code", "declineCode");

  if (readString(error, "type") === "invalid_request_error") {
    return { declineClass: "REQUEST_INVALID", processorCode: code };
  }

  // A client-side validation refusal is never a verdict on the card: nothing
  // was sent anywhere. An unmapped one falls to REQUEST_INVALID, whose copy is
  // worded as our problem, rather than to CARD_DECLINED.
  if (readString(error, "type") === "validation_error") {
    return {
      declineClass:
        (code ? STRIPE_CODE_CLASS[code] : undefined) ?? "REQUEST_INVALID",
      processorCode: code,
    };
  }

  // `code` first: Stripe sends `card_declined` as the code for every reason it
  // only names in `decline_code`, but for a card error like `expired_card` the
  // code is the specific one.
  const mapped =
    (code ? STRIPE_CODE_CLASS[code] : undefined) ??
    (declineCode ? STRIPE_DECLINE_CLASS[declineCode] : undefined);
  const processorCode = declineCode ?? code;

  if (mapped) return { declineClass: mapped, processorCode };
  return {
    declineClass: processorCode ? "CARD_DECLINED" : null,
    processorCode,
  };
};

/**
 * Turn whatever a processor said into a class and our own words.
 *
 * Never returns `null` and never throws: a payload we cannot read at all is
 * `UNKNOWN`, which is a real class with real copy, rather than an empty string
 * or the processor's raw text leaking onto the screen.
 */
export type DeclineAudience = "staff" | "patient";

/**
 * Whether the card was being charged or only saved.
 *
 * Every string in `MESSAGE` and `PATIENT_MESSAGE` ends by saying whether money
 * moved - that is the invariant both tables are written to, and
 * `decline-reason.test.ts` asserts it. On the save surfaces no charge was ever
 * attempted, so "No charge was made." answers a question nobody asked and
 * leaves the one they did ask - is the card on file or not - unanswered. The
 * reason sentences are identical either way, so this swaps the closing sentence
 * rather than duplicating thirty-six strings that would then drift.
 */
export type DeclinePurpose = "payment" | "save";

/** The closing sentence each table ends with, and its save-time replacement. */
export const OUTCOME_SENTENCE: Record<
  DeclineAudience,
  Record<DeclinePurpose, string>
> = {
  staff: {
    payment: "No charge was made.",
    save: "The card was not saved.",
  },
  patient: {
    payment: "You have not been charged.",
    save: "Your card was not saved.",
  },
};

const forPurpose = (
  message: string,
  audience: DeclineAudience,
  purpose: DeclinePurpose,
): string => {
  if (purpose === "payment") return message;
  const { payment, save } = OUTCOME_SENTENCE[audience];
  return message.endsWith(payment)
    ? `${message.slice(0, -payment.length)}${save}`
    : message;
};

export function normalizeDecline(
  processor: PaymentProcessor,
  input: unknown,
  audience: DeclineAudience = "staff",
  purpose: DeclinePurpose = "payment",
): PaymentDecline {
  const table = audience === "patient" ? PATIENT_MESSAGE : MESSAGE;
  const copy = (declineClass: DeclineClass) =>
    forPurpose(table[declineClass], audience, purpose);
  const payload = extractPayload(input);

  // The backend has already classified anything that came through our API, and
  // its answer wins - it saw the whole response, we see one field of it.
  const declared = readString(payload, "decline_class", "declineClass");
  if (declared && Object.prototype.hasOwnProperty.call(MESSAGE, declared)) {
    const declineClass = declared as DeclineClass;
    return {
      declineClass,
      message: copy(declineClass),
      processorCode: readString(payload, "code", "failure_code") ?? null,
    };
  }

  const { declineClass, processorCode } =
    processor === PaymentProcessor.STRIPE
      ? classifyStripe(payload)
      : classifyRainforest(payload);

  const resolved = declineClass ?? "UNKNOWN";
  return { declineClass: resolved, message: copy(resolved), processorCode };
}

/** The copy for a class we already hold. */
export const messageForDeclineClass = (declineClass: DeclineClass): string =>
  MESSAGE[declineClass];

/**
 * A few words for staff, for places with room for one line, such as a toast
 * after booking. `MESSAGE` holds the full explanation for checkout.
 */
const SHORT_REASON: Record<DeclineClass, string> = {
  EXPIRED_CARD: "The card has expired.",
  LOST_OR_STOLEN: "The card was reported lost or stolen.",
  FRAUD_SUSPECTED: "The bank flagged it as possible fraud.",
  CARD_NOT_SUPPORTED: "This card can't be used for this payment.",
  ACCOUNT_CLOSED: "The card's account is closed.",
  AUTHORIZATION_REVOKED: "The bank withdrew permission to charge this card.",
  PAYMENT_METHOD_UNAVAILABLE: "The saved card can no longer be used.",
  TOO_MANY_ATTEMPTS: "Too many recent attempts on this card.",
  INCORRECT_CVC: "The security code was rejected.",
  INCORRECT_NUMBER: "The card number was rejected.",
  INCORRECT_ZIP: "The ZIP code didn't match.",
  INSUFFICIENT_FUNDS: "Insufficient funds.",
  DO_NOT_HONOR: "The bank declined it.",
  CARD_DECLINED: "The card was declined.",
  AUTHENTICATION_REQUIRED:
    "The patient needs to verify the payment with their bank.",
  PROCESSOR_UNAVAILABLE: "The card processor couldn't complete it.",
  REQUEST_INVALID: "The payment couldn't be sent.",
  UNKNOWN: "The card was declined.",
};

export const shortReasonForDeclineClass = (
  declineClass: DeclineClass,
): string => SHORT_REASON[declineClass];

/**
 * Whether a value off the wire names a class we have copy for.
 *
 * A class we do not recognise is a deploy-skew problem, not a reason to render
 * whatever the processor wrote - callers fall back to their own default.
 */
export const isDeclineClass = (value: unknown): value is DeclineClass =>
  // `hasOwnProperty`, not `in`: `in` walks the prototype chain, so wire values
  // like "constructor", "toString" or "__proto__" would satisfy the guard and
  // `messageForDeclineClass` would then hand back an inherited function where a
  // string is declared - which React renders as "Object".
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(MESSAGE, value);

/**
 * Classes that mean a card was actually refused, rather than that something
 * on our side or the processor's went wrong.
 *
 * This is the distinction the surfaces need for a heading: "Payment declined"
 * is wrong, and reads as the patient's fault, when the truth is that we sent a
 * malformed request or the processor was down.
 */
const NOT_A_REFUSAL: ReadonlySet<DeclineClass> = new Set<DeclineClass>([
  "PROCESSOR_UNAVAILABLE",
  "REQUEST_INVALID",
  "UNKNOWN",
]);

export const isCardRefusal = (
  declineClass: DeclineClass | null | undefined,
): boolean => declineClass != null && !NOT_A_REFUSAL.has(declineClass);

/**
 * A rendered message from an API error's `detail`, whatever shape it is in.
 *
 * A refused charge returns `detail: {code, decline_class, message}`. Anything
 * that interpolated that straight into a toast or an `Error` printed
 * "[object Object]" at staff, or threw "Objects are not valid as a React child"
 * at a patient. Reads the class and answers in our own copy for the audience,
 * falls back to the processor's text, and returns null when there is neither so
 * the caller can use its own default.
 */
export function declineMessageFromDetail(
  detail: unknown,
  audience: DeclineAudience = "staff",
  purpose: DeclinePurpose = "payment",
): string | null {
  if (typeof detail === "string") return detail;
  if (!detail || typeof detail !== "object") return null;

  const payload = detail as Record<string, unknown>;
  const declared = payload.decline_class ?? payload.declineClass;
  if (isDeclineClass(declared)) {
    const table = audience === "patient" ? PATIENT_MESSAGE : MESSAGE;
    return forPurpose(table[declared], audience, purpose);
  }
  return typeof payload.message === "string" ? payload.message : null;
}
